import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  Box
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUIContext } from '../context/UIContext';
import logo from '../assets/logo512.png';

const Header = () => {
  const { isSidebarOpen, toggleSidebar, viewport } = useUIContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Features', href: '/#features' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'Dashboard', href: '/#dashboard' },
    { label: 'Hotspot Page', href: '/#hotspot-landing-page' },
    { label: 'Contact', href: '/#contact' },
    { label: 'Blog', href: '/blog' },
    { label: 'Docs', href: 'https://docs.nasradius.com', target: '_blank' },
    { label: 'Get a Free Trial', href: '#contact', isCTA: true },
  ];

  const handleNavClick = (event, href, target) => {
    if (target === '_blank') return;

    const isHashLink = href.startsWith('#') || href.startsWith('/#');
    if (!isHashLink) return;

    event.preventDefault();
    const targetId = href.replace('/#', '').replace('#', '');

    if (location.pathname !== '/') {
      navigate(`/#${targetId}`);
    } else {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (viewport === 'mobile' && isSidebarOpen) {
      toggleSidebar();
    }
  };

  const logoSize = isScrolled
    ? (viewport === 'mobile' ? '36px' : '48px')
    : (viewport === 'mobile' ? '44px' : '56px');

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{
        py: isScrolled ? 0.5 : 1,
        background: isScrolled
          ? 'rgba(242,246,250,0.82)'
          : 'transparent',
        backdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
        WebkitBackdropFilter: isScrolled ? 'blur(20px) saturate(180%)' : 'none',
        borderBottom: isScrolled ? '1px solid rgba(0,0,0,0.06)' : 'none',
        transition: 'all 400ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        zIndex: 1100,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            <img
              src={logo}
              alt="NasRadius Logo"
              style={{
                height: logoSize,
                transition: 'height 400ms cubic-bezier(0.4, 0.0, 0.2, 1)',
              }}
            />
          </Box>

          {viewport === 'mobile' ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={toggleSidebar}
            >
              <MenuIcon sx={{ fontSize: 28, color: '#0b74c1' }} />
            </IconButton>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: isScrolled ? '8px' : '16px',
                alignItems: 'center',
                transition: 'gap 400ms cubic-bezier(0.4, 0.0, 0.2, 1)',
              }}
            >
              {navItems.map((item, index) => (
                <Button
                  key={index}
                  href={item.href}
                  onClick={(event) => handleNavClick(event, item.href, item.target)}
                  variant={item.isCTA ? 'contained' : 'text'}
                  sx={
                    item.isCTA
                      ? {
                          fontWeight: 500,
                          letterSpacing: '-0.005em',
                          fontSize: isScrolled
                            ? { xs: '0.8rem', md: '0.85rem' }
                            : { xs: '0.85rem', md: '0.9rem' },
                          textTransform: 'none',
                          borderRadius: 980,
                          px: isScrolled ? 3 : 3.5,
                          py: isScrolled ? 0.75 : 1,
                          background: 'linear-gradient(135deg, #0a66bb 0%, #19b594 100%)',
                          color: '#ffffff',
                          boxShadow: '0 4px 12px rgba(10,102,187,0.2)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0b74c1 0%, #219cac 100%)',
                            transform: 'scale(1.02)',
                            boxShadow: '0 6px 16px rgba(10,102,187,0.3)',
                          },
                        }
                      : {
                          fontWeight: 450,
                          color: 'rgba(29,29,31,0.75)',
                          fontSize: isScrolled
                            ? { xs: '0.8rem', md: '0.85rem' }
                            : { xs: '0.85rem', md: '0.9rem' },
                          letterSpacing: '-0.005em',
                          textTransform: 'none',
                          position: 'relative',
                          transition: 'color 0.3s ease, font-size 400ms cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            color: '#0b74c1',
                            '&::after': { width: '100%' },
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 4,
                            left: 8,
                            width: 0,
                            height: '2px',
                            backgroundColor: '#0b74c1',
                            borderRadius: 1,
                            transition: 'width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          },
                        }
                  }
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Drawer
            anchor="right"
            open={isSidebarOpen}
            onClose={toggleSidebar}
            sx={{
              '& .MuiPaper-root': {
                background: 'rgba(245,245,247,0.95)',
                backdropFilter: 'blur(20px)',
              },
            }}
          >
            <Box
              sx={{ width: 280, height: '100%' }}
              role="presentation"
              onClick={toggleSidebar}
              onKeyDown={toggleSidebar}
            >
              <List sx={{ pt: 2 }}>
                {navItems.map((item, index) => (
                  <ListItem
                    button
                    key={index}
                    component="a"
                    href={item.href}
                    onClick={(event) => handleNavClick(event, item.href, item.target)}
                    sx={{
                      justifyContent: 'center',
                      py: 1.5,
                      ...(item.isCTA && {
                        mx: 3,
                        my: 1,
                        borderRadius: 980,
                        background: 'linear-gradient(135deg, #0a66bb 0%, #19b594 100%)',
                        color: '#ffffff',
                        boxShadow: '0 4px 12px rgba(10,102,187,0.2)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0b74c1 0%, #219cac 100%)',
                        },
                      }),
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: item.isCTA ? 500 : 450,
                          fontSize: '1rem',
                          letterSpacing: '-0.005em',
                          color: item.isCTA ? '#ffffff' : '#1d1d1f',
                          textAlign: 'center',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
